import type { Plugin } from 'vue';
import { useLocalStorage, useSessionStorage as useStorage } from '@vueuse/core';
import { config } from '@/config';
import { createResumableSession } from '@/stores/resumableSession';
import '@/stores/schemas';
import { useFlowStore } from '@/stores/useFlowStore';
import { useCustomerStore } from '@/stores/useCustomerStore';
import { useValidationStore } from '@/stores/useValidationStore';
import { useLoadingStore } from '@/stores/useLoadingStore';
import type { CustomerStoreState, LoadingStoreState, ValidationStoreState } from '@/stores/schemas';

// https://cube-nl.sentry.io/issues/4927931166/events/146e98377e904ec0bb8c688244818192/?project=4506579637960704
// Safari > Settings > Geavanceerd > Blokkeer alle cookies

const persistedData = new Map<string, unknown>();

/**
 * Persist data in internal browser session storage.
 *
 * This return type T is actually incorrect, useStorage returns a Ref<T>, but then
 * Zod will complain about it, that's why we return the value type of the ref itself.
 *
 * To use this persist function outside a Pinia store it must be wrapped in a ref:
 * `ref(persist('name', '')).value = 'example';`
 *
 * see https://github.com/vueuse/vueuse/issues/1496
 */
export const persist = <T>(id: string, defaultValue: T): T => {
	try {
		return useStorage(id, defaultValue) as unknown as T;
	} catch {
		return persistInMemory(id, defaultValue);
	}
};

let session: ReturnType<typeof createResumableSession> | undefined;
let abandonedReservationDropped = false;

const resumableSession = () => (session ??= createResumableSession(window.localStorage, config.channel));

const dropAbandonedReservationOnce = (): void => {
	if (abandonedReservationDropped) return;

	abandonedReservationDropped = true;
	if (resumableSession().hasExpired()) resumableSession().clear();
	resumableSession().touch();
};

/** Scoped per channel: a cart belongs to one channel on the server. */
export const persistResumable = <T>(id: string, defaultValue: T): T => {
	const key = `${config.channel}/${id}`;

	try {
		dropAbandonedReservationOnce();

		return useLocalStorage(key, defaultValue) as unknown as T;
	} catch {
		return persistInMemory(key, defaultValue);
	}
};

export const pushSessionExpiry = (): void => {
	try {
		resumableSession().touch();
	} catch {
		// Storage is unavailable, the in-memory fallback has no expiry to push forward.
	}
};

/**
 * Fallback for when the user blocks all cookies and storage is unavailable.
 */
const persistInMemory = <T>(id: string, defaultValue: T): T => {
	if (!persistedData.has(id)) {
		persistedData.set(id, defaultValue);
	}

	return persistedData.get(id) as T;
};

export const stores: Plugin = {
	install: ({ config }) => {
		config.globalProperties.$flowstore = useFlowStore();
		config.globalProperties.$customerstore = useCustomerStore();
		config.globalProperties.$validationstore = useValidationStore();
		config.globalProperties.$loadingstore = useLoadingStore();
	},
};

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		$flowstore: ReturnType<typeof useFlowStore>;
		$customerstore: CustomerStoreState;
		$validationstore: ValidationStoreState;
		$loadingstore: LoadingStoreState;
	}
}
